import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { SubMenuOption } from 'rangeConfig/@types';
import { useCore } from 'contexts/core';
import Box from 'ui/Box';
import { useSelector } from 'react-redux';
import { getMarketProducts, getSelectedGroupId } from 'ducks/products';
import SkapaTabs from 'ui/Tabs/SkapaTabs';
import { Tab } from '@ingka/tabs';
import styled from 'styled-components';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { getMenuTitle } from './utils/utils';
import { useBehaviorTracker } from 'contexts/behaviors';

type Props = {
  tabs: SubMenuOption[];
  intitallySelectedTabId?: string;
};

const Wrapper = styled.div`
  margin: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xxl} 0;
`;

const WidgetWrapper = styled(Box)`
  border: 0;
  margin: ${({ theme }) => theme.space.l} 0;
  padding: 0;
`;

export default function ProductsSubMenuTabs({ tabs, intitallySelectedTabId }: Props) {
  const { formatMessage } = useIntl();
  const [selectedTab, setSelectedTab] = useState(
    intitallySelectedTabId ? intitallySelectedTabId : tabs[0].id
  );

  const [tabWidgets, setTabWidgets] = useState(tabs[0].widgets);
  const selectedGroupId = useSelector(getSelectedGroupId);
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();

  const products = useSelector(getMarketProducts);

  const tabClicked = (tabId: string) => {
    const nextTab = tabs.find(t => t.id === tabId);
    const previousTab = tabs.find(t => t.id === selectedTab);
    if (!nextTab) return;

    if (previousTab) {
      behaviorTracker
        .withContext({ subMenuId: selectedTab, groupId: selectedGroupId, menuType: 'main' })
        .trigger('subMenuExited', null, previousTab.behaviors);
    }
    behaviorTracker
      .withContext({ subMenuId: tabId, groupId: selectedGroupId, menuType: 'main' })
      .trigger('subMenuEntered', null, nextTab.behaviors);
    setSelectedTab(tabId);
  };

  useEffect(() => {
    const tab = tabs.find(t => t.id === selectedTab);
    if (!tab) return;
    setTabWidgets(tab.widgets);
  }, [tabs, selectedTab]);

  useEffect(() => {
    setSelectedTab(intitallySelectedTabId ? intitallySelectedTabId : tabs[0].id);
  }, [selectedGroupId, tabs, intitallySelectedTabId]);

  return (
    <>
      <Box
        mx="xxl"
        style={{ position: 'sticky', top: '5rem', zIndex: 200, backgroundColor: 'white' }}
      >
        {tabs && tabWidgets && (
          <SkapaTabs
            key={tabs[0]?.id}
            data-testid="front-tabs"
            defaultActiveTab={intitallySelectedTabId || tabs[0].id}
            onTabChanged={tabId => tabClicked(tabId)}
            tabs={tabs.map(tab => (
              <Tab
                key={tab.id}
                tabPanelId={tab.id}
                data-testid={tab.id}
                text={formatMessage({ id: `${tab.title}` })}
                disabled={tab.disabled ? tab.disabled(coreApi) : false}
              />
            ))}
          />
        )}
      </Box>
      <Wrapper>
        {tabWidgets &&
          tabWidgets
            .filter(w => (w.hidden ? !w.hidden(coreApi, products) : true))
            .map(({ id, component: Component, title }) => (
              <WidgetWrapper data-testid={'widget-wrapper-' + id} key={id}>
                {title && (
                  <Title type={ThemeFontStyleTypeEnum.headingSmall}>
                    {getMenuTitle(title, undefined, formatMessage, coreApi)}
                  </Title>
                )}
                <Component key={id} />
              </WidgetWrapper>
            ))}
      </Wrapper>
    </>
  );
}
